import type { INotificacion } from '@/modules/Core/interfaces'
import Notiflix from 'notiflix'
import type { App } from 'vue'

const notificacionTypes: { [key: string]: (message: string) => void } = {
  success: Notiflix.Notify.success,
  failure: Notiflix.Notify.failure,
  warning: Notiflix.Notify.warning,
  info: Notiflix.Notify.info
}

type ConfirmacionExtraOptions = {
  closable?: boolean
  onClose?: () => void
}

const CONFIRM_WRAP_ID = 'NotiflixConfirmWrap'
const CONFIRM_CONTENT_SELECTOR = '.notiflix-confirm-content'
const CONFIRM_CLOSE_BUTTON_CLASS = 'notiflix-confirm-close-btn'

const getAnimationDuration = (element?: HTMLElement | null) => {
  if (!element || typeof window === 'undefined') {
    return 300
  }

  const parseDuration = (value?: string) => {
    if (!value) return NaN
    const numeric = parseFloat(value)
    if (Number.isNaN(numeric)) {
      return NaN
    }
    return value.includes('ms') ? numeric : numeric * 1000
  }

  const inline = parseDuration(element.style.animationDuration)
  if (!Number.isNaN(inline) && inline > 0) {
    return inline
  }

  const computed = parseDuration(window.getComputedStyle(element).animationDuration)
  if (!Number.isNaN(computed) && computed > 0) {
    return computed
  }

  return 300
}

const closeActiveConfirm = (wrap: HTMLElement, onClose?: () => void) => {
  wrap.classList.add('nx-remove')
  const content = wrap.querySelector(CONFIRM_CONTENT_SELECTOR) as HTMLElement | null
  const timeoutMs = getAnimationDuration(content)

  window.setTimeout(() => {
    wrap.parentNode?.removeChild(wrap)
    if (typeof onClose === 'function') {
      onClose()
    }
  }, timeoutMs)
}

const attachConfirmCloseButton = (onClose?: () => void, attempts = 0) => {
  if (typeof document === 'undefined') {
    return
  }

  const wrap = document.getElementById(CONFIRM_WRAP_ID)
  if (!wrap) {
    if (attempts < 5) {
      window.setTimeout(() => attachConfirmCloseButton(onClose, attempts + 1), 25)
    }
    return
  }

  const content = wrap.querySelector(CONFIRM_CONTENT_SELECTOR) as HTMLElement | null
  if (!content) {
    return
  }

  if (content.querySelector(`.${CONFIRM_CLOSE_BUTTON_CLASS}`)) {
    return
  }

  const computedPosition = window.getComputedStyle(content).position
  if (!content.style.position && computedPosition === 'static') {
    content.style.position = 'relative'
  }

  const closeBtn = document.createElement('button')
  closeBtn.type = 'button'
  closeBtn.innerHTML = '&times;'
  closeBtn.className = CONFIRM_CLOSE_BUTTON_CLASS
  closeBtn.setAttribute('aria-label', 'Cerrar confirmación')
  closeBtn.style.position = 'absolute'
  closeBtn.style.top = '8px'
  closeBtn.style.right = '8px'
  closeBtn.style.border = 'none'
  closeBtn.style.background = 'transparent'
  closeBtn.style.color = '#4b5563'
  closeBtn.style.fontSize = '20px'
  closeBtn.style.fontWeight = '600'
  closeBtn.style.cursor = 'pointer'
  closeBtn.style.width = '24px'
  closeBtn.style.height = '24px'
  closeBtn.style.display = 'flex'
  closeBtn.style.alignItems = 'center'
  closeBtn.style.justifyContent = 'center'
  closeBtn.style.lineHeight = '1'

  closeBtn.addEventListener('mouseenter', () => {
    closeBtn.style.color = '#1f2937'
  })
  closeBtn.addEventListener('mouseleave', () => {
    closeBtn.style.color = '#4b5563'
  })
  closeBtn.addEventListener('click', () => closeActiveConfirm(wrap, onClose))

  content.appendChild(closeBtn)
}
 export const notificacion: INotificacion = {
  confirmacion(
    titulo: string,
    mensaje: string,
    okCb: () => void,
    cancelCb: () => void,
    color: string = '#193cb9',
    plainText: boolean = true,
    messageMaxLength: number = 110,
    message_ok: string = 'Ok',
    message_cancel: string = 'Cancelar',
    titleMaxLength: number = 50,
    extraOptions: ConfirmacionExtraOptions = {},
  ) {
    const { closable = false, onClose } = extraOptions
    Notiflix.Confirm.init({
      titleColor: color,
      plainText: plainText,
      messageMaxLength: messageMaxLength,
      titleMaxLength: titleMaxLength,
      okButtonColor: '#ffffff',
      okButtonBackground: '#193cb9',
    })
    Notiflix.Confirm.show(
      titulo,
      mensaje,
      message_ok,
      message_cancel,
      okCb,
      cancelCb
    )

    if (closable) {
      attachConfirmCloseButton(onClose)
    }
  },

  loading() {
    Notiflix.Loading.pulse({
      zindex: 9999999,
      svgColor: '#193cb9'
    })
    return Notiflix.Loading
  },

  notificacion(tipo, mensaje, tiempo = 4000, closeButton = false) {
    Notiflix.Notify.init({
      zindex: 99999,
      timeout: tiempo,
      closeButton,
      cssAnimationStyle: 'from-top'
    })
    const notifyFunc = notificacionTypes[tipo.toLowerCase()] || Notiflix.Notify.info
    notifyFunc(mensaje)
  },

  report(tipo, titulo, mensaje, okCb) {
    Notiflix.Report.init({
      zindex: 999999,
      plainText: false
    })
    Notiflix.Report[tipo](titulo, mensaje, 'Ok', okCb)
  }
}

export default {
  install (app: App) {
    app.config.globalProperties.$notificacion = notificacion
    app.provide('$notificacion', notificacion)
  }
}
